import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  IconBuildingBank,
  IconCreditCard,
  IconWallet,
  IconQrcode,
  IconCreditCardPay,
} from '@tabler/icons-react';
import { toast } from 'react-hot-toast';
import qs from 'qs';

import { get, post } from '@network/api';
import { getErrorMessage } from '@lib/handle-error';
import {
  DataDetailCheckout,
  SettingsProps,
  ItemPayment,
  PaymentMethodConfig,
  PaymentMethodResponse,
  PaymentMethodKey,
} from '@pages/types';
import { useCheckoutStep } from '@pages/hooks/useCheckoutStep';
import { useCheckoutStore } from '@stores/useCheckoutStore';
import FormDirectDebitBTN from '@components/FormDirectDebit/FormDirectDebitBTN';
import DanaBinding from '@components/Result/DanaBinding';
import PhoneNumber from '@components/Result/PhoneNumber';

import CardPaymentMethod from '@components/CardPaymentMethod';
import Spinner from '@components/Spinner';
import BusinessName from '../BusinessName';

interface PaymentMethodProps {
  data: DataDetailCheckout;
  settings: SettingsProps;
  onPaymentSelected?: () => void;
  onRefreshData: () => Promise<unknown>;
}

const PAYMENT_METHODS: PaymentMethodConfig[] = [
  {
    key: 'va',
    name: 'BANK',
    type: 'VIRTUAL_ACCOUNT',
    icon: <IconBuildingBank className="h-[22px] w-[22px] text-gray-800" />,
  },
  {
    key: 'cards',
    name: 'CREDIT CARD',
    type: 'CARD',
    icon: <IconCreditCard className="h-[22px] w-[22px] text-gray-800" />,
  },
  {
    key: 'ewallet',
    name: 'E-WALLET',
    type: 'E-WALLET',
    icon: <IconWallet className="h-[22px] w-[22px] text-gray-800" />,
  },
  {
    key: 'direct_debit',
    name: 'DEBIT CARD',
    type: 'DIRECT_DEBIT',
    icon: <IconCreditCardPay className="h-[22px] w-[22px] text-gray-800" />,
  },
  {
    key: 'qris',
    name: 'QRIS',
    type: 'QRIS',
    icon: <IconQrcode className="h-[22px] w-[22px] text-gray-800" />,
  },
];

const EXCLUDED_PAYMENT_CODES = ['group_commission'];

const LoadingMethods = () => (
  <div className="flex flex-col gap-2">
    {[...Array(3)].map((_, i) => (
      <div key={i} className="h-16 animate-pulse rounded-lg bg-gray-200" />
    ))}
  </div>
);

const PaymentMethod: React.FC<PaymentMethodProps> = ({
  data,
  settings,
  onPaymentSelected,
  onRefreshData,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = qs.parse(location.search, { ignoreQueryPrefix: true });
  const { id, payment_type, type, payment_method_id, service_id } = query as {
    id?: string;
    payment_type?: string;
    type?: string;
    payment_method_id?: string;
    service_id?: string;
  };
  const { goToOverview } = useCheckoutStep();
  const {
    lastSelectedPaymentId,
    lastSelectedPaymentIndex,
    setLastSelectedPayment,
    setSelectedPaymentKey,
    setSubmittedCustomerNo,
    submittedCustomerNo,
  } = useCheckoutStore();

  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [selectedPaymentType, setSelectedPaymentType] =
    useState<ItemPayment | null>(null);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const [showDirectDebitView, setShowDirectDebitView] = useState(false);
  const [showPhoneNumberView, setShowPhoneNumberView] = useState(false);
  const [showDanaBoundInfoView, setShowDanaBoundInfoView] = useState(false);
  const [accountCC, setAccountCC] = useState('');
  const [danaBalanceValue, setDanaBalanceValue] = useState('');
  const [danaBalanceCurrency, setDanaBalanceCurrency] = useState('IDR');
  const [danaBalanceError, setDanaBalanceError] = useState('');
  const [danaBalanceLoading, setDanaBalanceLoading] = useState(false);
  const [danaBalanceFetched, setDanaBalanceFetched] = useState(false);

  const { data: paymentMethodResponse, isLoading } =
    useQuery<PaymentMethodResponse>({
      queryKey: ['getDetailMethodPayment', id],
      queryFn: async () => {
        if (!id) throw new Error('Invoice ID is required');
        return get(`/checkout/v1/payment-method?invoice_number=${id}`);
      },
      retry: false,
      enabled: !!id,
    });

  const getFilteredPaymentMethods = (key: PaymentMethodKey): ItemPayment[] => {
    const items = paymentMethodResponse?.data?.[key] || [];
    return items.filter((item) => !EXCLUDED_PAYMENT_CODES.includes(item.code));
  };

  // Skip API when user re-selects the exact same method that is already created.
  const isSameAsPreviousPayment = (): boolean => {
    const sameMethodById =
      lastSelectedPaymentId !== null &&
      selectedPaymentType !== null &&
      selectedPaymentType.id === lastSelectedPaymentId;

    const sameMethodByCode =
      !payment_type ||
      (selectedPaymentType?.code || '').toLowerCase() ===
        (payment_type || '').toLowerCase();

    return (
      sameMethodById && sameMethodByCode && data?.is_payment_created === true
    );
  };

  const updateUrlWithPaymentMethod = (): void => {
    if (!selectedPaymentType) return;

    const newQuery = { ...query } as typeof query & {
      payment_type?: string;
      type?: string;
      payment_method_id?: string;
      service_id?: string;
    };

    // Use code as payment_type (e.g. 'bni_va', 'bca_va', 'qris'); fallback to type if code is empty
    const paymentCode = (
      selectedPaymentType.code ||
      selectedPaymentType.type ||
      ''
    ).toLowerCase();
    if (paymentCode) {
      newQuery.payment_type = paymentCode;
    }
    if (selectedPaymentType.type) {
      newQuery.type = selectedPaymentType.type;
    }
    newQuery.payment_method_id = String(selectedPaymentType.id);
    newQuery.service_id = selectedPaymentType.service_id;

    const search = qs.stringify(newQuery, { addQueryPrefix: true });
    navigate({ search }, { replace: true });
  };

  const findPaymentFromQuery = useCallback(() => {
    for (let i = 0; i < PAYMENT_METHODS.length; i += 1) {
      const method = PAYMENT_METHODS[i];
      const items = getFilteredPaymentMethods(method.key);
      if (!items.length) continue;

      const matchedById = payment_method_id
        ? items.find((item) => String(item.id) === String(payment_method_id))
        : undefined;
      if (matchedById) {
        return { index: i, item: matchedById };
      }

      const matchedByService = service_id
        ? items.find((item) => item.service_id === service_id)
        : undefined;
      if (matchedByService) {
        return { index: i, item: matchedByService };
      }

      // Match by code (e.g. 'bni_va', 'bca_va', 'qris')
      const matchedByCode = payment_type
        ? items.find(
            (item) => item.code?.toLowerCase() === payment_type.toLowerCase()
          )
        : undefined;
      if (matchedByCode) {
        return { index: i, item: matchedByCode };
      }

      const matchedByName = payment_type
        ? items.find(
            (item) => item.name?.toLowerCase() === payment_type.toLowerCase()
          )
        : undefined;
      if (matchedByName) {
        return { index: i, item: matchedByName };
      }

      const matchedByType = type
        ? items.find((item) => item.type?.toLowerCase() === type.toLowerCase())
        : undefined;
      if (matchedByType) {
        return { index: i, item: matchedByType };
      }
    }

    const paymentString = data?.payment?.string?.toLowerCase();

    if (paymentString === 'va') {
      const bankCode = data?.payment?.va?.bank?.toLowerCase();
      const vaIndex = PAYMENT_METHODS.findIndex(
        (method) => method.key === 'va'
      );

      if (vaIndex >= 0) {
        const vaItems = getFilteredPaymentMethods('va');
        const matchedVa = vaItems.find((item) => {
          const itemCode = item.code?.toLowerCase();
          const itemName = item.name?.toLowerCase();
          return bankCode && (itemCode === bankCode || itemName === bankCode);
        });

        if (matchedVa) {
          return { index: vaIndex, item: matchedVa };
        }
      }
    }

    if (paymentString === 'qris') {
      const qrisIndex = PAYMENT_METHODS.findIndex(
        (method) => method.key === 'qris'
      );

      if (qrisIndex >= 0) {
        const qrisItems = getFilteredPaymentMethods('qris');
        if (qrisItems.length > 0) {
          return { index: qrisIndex, item: qrisItems[0] };
        }
      }
    }

    return null;
  }, [
    payment_method_id,
    payment_type,
    service_id,
    type,
    paymentMethodResponse,
    data,
  ]);

  const submitPayment = async (customerNo = ''): Promise<void> => {
    if (!id) {
      toast.error('Invoice ID is missing');
      return;
    }

    if (!selectedPaymentType) return;

    const payload = {
      type: selectedPaymentType.type,
      service_id: selectedPaymentType.service_id,
      customer_no: customerNo,
      payment_method_id: selectedPaymentType.id,
    };

    // Always update URL first so payment_type param is current
    updateUrlWithPaymentMethod();

    // If the same payment was previously submitted, skip API and go directly to result.
    if (isSameAsPreviousPayment()) {
      setLastSelectedPayment(selectedPaymentType.id, openIndex);
      setSelectedPaymentKey(
        (
          selectedPaymentType.code ||
          selectedPaymentType.type ||
          ''
        ).toLowerCase() || null
      );
      onPaymentSelected?.();
      return;
    }

    setIsLoadingSubmit(true);

    try {
      const response = await post(
        `/checkout/v1/payment-method?invoice_number=${id}`,
        payload
      );

      // Open redirect URL immediately on first submission (ewallet / CC)
      const redirectUrl: string | undefined =
        response?.data?.h2h_payment_info?.web_redirect_url ||
        response?.h2h_payment_info?.web_redirect_url;
      if (redirectUrl) {
        if (redirectUrl.startsWith('http')) {
          window.open(redirectUrl, '_blank');
        } else {
          window.location.href = redirectUrl;
        }
      }

      setLastSelectedPayment(selectedPaymentType.id, openIndex);
      setSelectedPaymentKey(
        (
          selectedPaymentType.code ||
          selectedPaymentType.type ||
          ''
        ).toLowerCase() || null
      );
      if (customerNo) setSubmittedCustomerNo(customerNo);
      await onRefreshData?.();
      onPaymentSelected?.();
    } catch (error: unknown) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoadingSubmit(false);
    }
  };

  useEffect(() => {
    if (!paymentMethodResponse?.data) return;

    // Prioritize last selection stored in Zustand (persists across method ↔ payment navigation)
    if (lastSelectedPaymentId !== null && lastSelectedPaymentIndex !== null) {
      const method = PAYMENT_METHODS[lastSelectedPaymentIndex];
      if (method) {
        const items = getFilteredPaymentMethods(method.key);
        const item = items.find((i) => i.id === lastSelectedPaymentId);
        if (item) {
          setOpenIndex(lastSelectedPaymentIndex);
          setSelectedPaymentType(item);
          return;
        }
      }
    }

    // Fallback: restore from URL params (e.g. deep link or reload)
    const matched = findPaymentFromQuery();
    if (!matched) return;

    setOpenIndex(matched.index);
    setSelectedPaymentType(matched.item);
  }, [
    paymentMethodResponse,
    findPaymentFromQuery,
    lastSelectedPaymentId,
    lastSelectedPaymentIndex,
  ]);

  useEffect(() => {
    if (openIndex === null) return;

    const method = PAYMENT_METHODS[openIndex];
    if (!method) return;

    const items = getFilteredPaymentMethods(method.key);
    if (!items.length) return;

    const hasSelectedItem = items.some(
      (item) => item.id === selectedPaymentType?.id
    );

    if (!hasSelectedItem) {
      setSelectedPaymentType(items[0]);
    }
  }, [openIndex, selectedPaymentType, paymentMethodResponse]);

  useEffect(() => {
    if (data?.checkout_state !== 'ACCOUNT_BINDING_SUCCESS') return;
    if (!paymentMethodResponse?.data) return;

    const danaMethodIndex = PAYMENT_METHODS.findIndex(
      (method) => method.key === 'ewallet'
    );

    if (danaMethodIndex < 0) return;

    const ewalletItems = getFilteredPaymentMethods('ewallet');
    const danaItem = ewalletItems.find((item) => {
      const code = (item.code || '').toLowerCase();
      const name = (item.name || '').toLowerCase();
      return code.includes('dana') || name.includes('dana');
    });

    if (!danaItem) return;

    setOpenIndex(danaMethodIndex);
    setSelectedPaymentType(danaItem);
    setShowDirectDebitView(false);
    setShowPhoneNumberView(false);
    setShowDanaBoundInfoView(true);

    if (!submittedCustomerNo && data?.customer?.phone) {
      setSubmittedCustomerNo(data.customer.phone);
    }
  }, [
    data?.checkout_state,
    data?.customer?.phone,
    paymentMethodResponse,
    submittedCustomerNo,
    setSubmittedCustomerNo,
  ]);

  const handleToggle = (index: number): void => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const handlePaymentSelect = (payment: ItemPayment | undefined): void => {
    setSelectedPaymentType(payment ?? null);
  };

  // 'CARD' covers CC items where API returns type:'CARD'; 'CARDS' covers type:'CARDS'
  const PHONE_REQUIRED_TYPES: readonly string[] = ['CARDS', 'CARD', 'EWALLET'];

  // Normalize 'card' → 'cards' so both API formats compare equally
  const normalizeCardType = (t: string): string => (t === 'card' ? 'cards' : t);

  const handleSubmit = async (): Promise<void> => {
    if (!selectedPaymentType) {
      toast.error('Please select a payment method');
      return;
    }

    if (selectedPaymentType.type.toUpperCase() === 'DIRECT_DEBIT') {
      setShowDirectDebitView(true);
      return;
    }

    if (PHONE_REQUIRED_TYPES.includes(selectedPaymentType.type.toUpperCase())) {
      const danaSelected = isDanaPaymentMethod();

      if (danaSelected && data?.account_binding?.is_bound) {
        setShowDanaBoundInfoView(true);
        setShowPhoneNumberView(false);
        return;
      }

      const rawCategory = (data?.payment?.type || '').toLowerCase();
      const existingPaymentCategory = normalizeCardType(rawCategory);
      const selectedType = normalizeCardType(
        selectedPaymentType.type.toLowerCase()
      );

      // Same payment method & same code (e.g. same CC card) → skip phone, go to result
      const isSamePaymentAlreadyCreated =
        data?.is_payment_created === true &&
        existingPaymentCategory === selectedType &&
        selectedPaymentType.code.toLowerCase() ===
          (payment_type || '').toLowerCase();

      if (isSamePaymentAlreadyCreated) {
        setSelectedPaymentKey(selectedPaymentType.code);
        onPaymentSelected?.();
        return;
      }

      setShowDanaBoundInfoView(false);
      setShowPhoneNumberView(true);
      return;
    }

    await submitPayment();
  };

  const handlePhoneConfirm = (phoneNumber: string): void => {
    setShowPhoneNumberView(false);
    submitPayment(phoneNumber);
  };

  const isDanaPaymentMethod = (): boolean => {
    if (!selectedPaymentType) return false;

    const paymentCode = (selectedPaymentType.code || '').toLowerCase();
    const paymentName = (selectedPaymentType.name || '').toLowerCase();

    return paymentCode.includes('dana') || paymentName.includes('dana');
  };

  const handleInquiryBalance = async (): Promise<void> => {
    if (!id) {
      toast.error('Invoice ID is missing');
      return;
    }

    const phoneNo = submittedCustomerNo || data?.customer?.phone;
    if (!phoneNo) {
      toast.error('Phone number is missing');
      return;
    }

    setDanaBalanceLoading(true);
    setDanaBalanceError('');
    setDanaBalanceFetched(false);

    try {
      const response = await post(
        '/checkout/v1/payment/direct-debit/inquiry-balance',
        {
          invoice_number: id,
          phone_no: phoneNo,
        }
      );

      const responseData = response?.data;
      const isSuccessResponse =
        Number(response?.code || 0) === 200 ||
        String(response?.status || '').toLowerCase() === 'success';

      if (!isSuccessResponse) {
        throw new Error(
          responseData?.responseMessage ||
            response?.message ||
            'Failed to inquire balance'
        );
      }

      const balanceInfo = responseData?.accountInfos?.find(
        (item: { balanceType?: string }) => item.balanceType === 'BALANCE'
      );

      setDanaBalanceValue(balanceInfo?.amount?.value || '0');
      setDanaBalanceCurrency(balanceInfo?.amount?.currency || 'IDR');
      setDanaBalanceFetched(true);
    } catch (error: unknown) {
      setDanaBalanceError(
        error instanceof Error ? error.message : 'Failed to inquire balance'
      );
      setDanaBalanceFetched(false);
    } finally {
      setDanaBalanceLoading(false);
    }
  };

  const handleBindAccount = async (phoneNumber: string): Promise<void> => {
    if (!id) {
      toast.error('Invoice ID is missing');
      return;
    }

    setIsLoadingSubmit(true);

    try {
      setSubmittedCustomerNo(phoneNumber);

      const response = await post('/checkout/v1/payment/account-bind', {
        invoice_number: id,
        phone_no: phoneNumber,
      });

      const redirectUrl: string | undefined = response?.data?.redirectUrl;

      if (redirectUrl) {
        window.open(redirectUrl, '_blank');
        return;
      }

      setShowPhoneNumberView(false);
      setShowDanaBoundInfoView(true);
      await onRefreshData?.();
    } catch (error: unknown) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoadingSubmit(false);
    }
  };

  const goBackToPaymentMethods = (): void => {
    setShowDirectDebitView(false);
    setShowPhoneNumberView(false);
    setShowDanaBoundInfoView(false);
    setDanaBalanceValue('');
    setDanaBalanceCurrency('IDR');
    setDanaBalanceError('');
    setDanaBalanceFetched(false);

    // Restore last selected payment from store
    if (
      lastSelectedPaymentId !== null &&
      lastSelectedPaymentIndex !== null &&
      paymentMethodResponse?.data
    ) {
      const method = PAYMENT_METHODS[lastSelectedPaymentIndex];
      if (method) {
        const items = getFilteredPaymentMethods(method.key);
        const item = items.find((i) => i.id === lastSelectedPaymentId);
        if (item) {
          setOpenIndex(lastSelectedPaymentIndex);
          setSelectedPaymentType(item);
          return;
        }
      }
    }

    setSelectedPaymentType(null);
    setOpenIndex(null);
  };

  if (showDanaBoundInfoView && selectedPaymentType !== null) {
    return (
      <DanaBinding
        data={data}
        settings={settings}
        accountBinding={data?.account_binding}
        submittedCustomerNo={submittedCustomerNo}
        danaBalanceValue={danaBalanceValue}
        danaBalanceCurrency={danaBalanceCurrency}
        danaBalanceError={danaBalanceError}
        danaBalanceFetched={danaBalanceFetched}
        danaBalanceLoading={danaBalanceLoading}
        isLoadingSubmit={isLoadingSubmit}
        onGetBalance={handleInquiryBalance}
        onContinuePayment={() =>
          submitPayment(submittedCustomerNo || data?.customer?.phone || '')
        }
        onGoBack={goBackToPaymentMethods}
      />
    );
  }

  if (showPhoneNumberView && selectedPaymentType !== null) {
    const paymentCode = (selectedPaymentType.code || '').toLowerCase();
    const paymentName = (selectedPaymentType.name || '').toLowerCase();
    const isDanaMethod =
      paymentCode.includes('dana') || paymentName.includes('dana');

    return (
      <div className="mb-5 flex flex-col gap-4 px-3 pt-8 md:mb-0 md:pb-4 lg:px-0">
        <PhoneNumber
          data={data}
          settings={settings}
          onConfirm={handlePhoneConfirm}
          onBindAccount={handleBindAccount}
          isDanaMethod={isDanaMethod}
          onGoBack={() => setShowPhoneNumberView(false)}
          isLoading={isLoadingSubmit}
        />
      </div>
    );
  }

  if (
    showDirectDebitView &&
    selectedPaymentType?.type.toUpperCase() === 'DIRECT_DEBIT'
  ) {
    return (
      <div className="mb-5 flex flex-col gap-4 px-3 pt-8 md:mb-0 md:pb-4 lg:px-0">
        <BusinessName
          data={data}
          title="Debit Card Payment"
          subtitle="Complete your debit card payment information"
        />

        <div className="rounded-xl border border-gray-300 bg-gray-100 p-5 shadow-sm">
          <FormDirectDebitBTN
            items={selectedPaymentType}
            active={true}
            settings={settings}
            accountCC={accountCC}
            setAccountCC={setAccountCC}
            onPaymentSelected={onPaymentSelected}
            onGoBack={goBackToPaymentMethods}
          />
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="mb-5 flex flex-col gap-4 px-3 pt-8 md:mb-0 md:pb-4 lg:px-0">
        <BusinessName
          data={data}
          title="Choose Payment Method"
          subtitle="Loading available payment methods..."
        />
        <LoadingMethods />
      </div>
    );
  }

  return (
    <div className="mb-5 flex flex-col gap-4 px-3 pt-8 md:mb-0 md:pb-4 lg:px-0">
      <BusinessName
        data={data}
        title="Choose Payment Method"
        subtitle="Select your preferred payment method below"
      />

      {/* Payment Methods */}
      {paymentMethodResponse?.data ? (
        <div className="flex flex-col gap-1.5">
          {PAYMENT_METHODS.map((method, index) => {
            const items = getFilteredPaymentMethods(method.key);
            if (!items.length) return null;

            return (
              <CardPaymentMethod
                key={method.key}
                dataImg={items}
                name={method.name}
                type={method.type}
                icon={method.icon}
                isOpen={openIndex === index}
                onToggle={() => handleToggle(index)}
                settings={settings}
                selectedPaymentType={selectedPaymentType ?? undefined}
                setSelectedPaymentType={handlePaymentSelect}
              />
            );
          })}
        </div>
      ) : (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center text-gray-600">
          No payment methods available
        </div>
      )}

      {/* Action Buttons */}
      <div className="fixed bottom-1 left-0 right-0 z-20 bg-white px-4 py-3 md:relative md:px-0">
        <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
          <button
            onClick={goToOverview}
            disabled={isLoadingSubmit}
            className="w-full rounded-lg border border-gray-300 bg-gray-50 px-4 py-3 font-semibold text-gray-900 transition-all hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Back
          </button>

          <button
            onClick={handleSubmit}
            disabled={!selectedPaymentType || isLoadingSubmit}
            className="flex w-full items-center justify-center rounded-lg px-4 py-3 font-semibold transition-all hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            style={{
              backgroundColor: settings.button_color
                ? settings.button_color
                : '#5D87FF',
              color: settings.button_text_color
                ? settings.button_text_color
                : '#FFFFFF',
            }}
          >
            {isLoadingSubmit ? <Spinner /> : 'Choose Payment Method'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethod;
